import * as React from 'react'
import {format} from 'date-fns'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faCheckCircle, faClock, faTimesCircle} from '@fortawesome/free-solid-svg-icons'
import {type IconProp} from '@fortawesome/fontawesome-svg-core'
import {cn} from '@frontrow/console/src/lib/utils'
import {
  capitalizeFirstLetter,
  snakeCaseToSpaces
} from '@frontrow/console/src/entities/helpers/string-helper'

import {type TeacherLicenseAllocationT} from '@frontrow/console/src/models/teacher-license-allocation'
import DeleteLicenseButtonModal from '@frontrow/console/src/components/pages/teachers-page/teacher-licenses/license-row/delete-license-button-modal'

type Props = {
  license: TeacherLicenseAllocationT
  removeLicense: () => void
}

// The column headings in teacher-licenses.tsx use the same template. Every row
// sharing one template is what makes the dates line up down the page; each row
// sizing its own cells is what stopped them lining up before.
export const LICENSE_GRID = 'grid grid-cols-[11rem_8rem_8rem_7rem_2rem] items-center gap-x-4'

type StatusT = {
  label: string
  icon: IconProp
  iconClass: string
}

// Color repeats what the word already says, as in Alert, so the status does
// not depend on being able to tell green from grey.
function getStatus(license: TeacherLicenseAllocationT, now: Date): StatusT {
  if (now > license.expiresAt) {
    return {label: 'Expired', icon: faTimesCircle, iconClass: 'text-muted-text'}
  }
  if (now < license.startsAt) {
    return {label: 'Scheduled', icon: faClock, iconClass: 'text-warning-brand-dark'}
  }
  return {label: 'Active', icon: faCheckCircle, iconClass: 'text-success'}
}

export default function LicenseRow(props: Props): React.ReactElement {
  const {license, removeLicense} = props

  const startDateDetail = format(license.startsAt, 'MM/dd/yyyy')
  const endDateDetail = format(license.expiresAt, 'MM/dd/yyyy')
  const subjectDetail = capitalizeFirstLetter(snakeCaseToSpaces(license.subject))
  const status = getStatus(license, new Date())

  return (
    <div
      className={cn(
        LICENSE_GRID,
        'px-5 py-2.5 border-b border-border last:border-b-0 text-sm text-body'
      )}
    >
      <div className="font-semibold">{subjectDetail}</div>
      <div>{startDateDetail}</div>
      <div>{endDateDetail}</div>
      <div className="flex items-center gap-1.5">
        <FontAwesomeIcon
          icon={status.icon}
          className={status.iconClass}
          style={{width: 12}}
          aria-hidden="true"
        />
        {status.label}
      </div>
      <div>
        <DeleteLicenseButtonModal subjectDetail={subjectDetail} removeLicense={removeLicense} />
      </div>
    </div>
  )
}
